import { useKeyHints } from "../../game/touch/use-key-hints.ts";
import { useEffect, useRef, useState, useSyncExternalStore, type FocusEvent, type KeyboardEvent, type MouseEvent, type PointerEvent } from "react";
import { say } from "../../words.ts";
import { cssPerLogical } from "./measure.ts";
import { WINDOW_LIMITS, dragPlace, drawFrame, hasWindowFrame, keyResizeBox, keyStep, movePlace, resizeBox, resizeWishes, type FrameBox, type FrameWishes, type FramedWindowId, type PlaceRoom, type PlaceSize, type ResizeEdge, type WindowPlace } from "./place.ts";

/**
 * The frame of a game window (P1.9-T182, ADR-061's amendment "every game window moves and resizes"; it grows out of
 * P1.9-T97's move by the title bar). A framed window is moved by its title bar and resized from its four edges, its
 * four corners and its grip, each built as the chat panel's grip is: a drag changes the window by the pointer's
 * travel, the pointer captured for the drag alone, so nothing listens while the window rests, and a press takes no
 * focus and selects no text; a press on a button in the title bar is that button's and starts nothing.
 *
 * - The focused title takes the arrow keys, a logical pixel a press and 8 with Shift, and Home, which returns the
 *   window to where its layout puts it, as a double click on the title bar does.
 * - The focused grip, the window's last tab stop, takes the same arrows as a change of size (one step where the
 *   window has a step) and Home, which returns the window to its layout's size.
 * - Each key is prevented and stopped, so neither the input router nor the game acts on it, and no other key is
 *   taken.
 * - A resize moves at most once a frame, however many pointer moves arrive, so one resize costs one re-render of the
 *   window a frame.
 * - A drag ends where the pointer is released, and also when the browser takes the pointer away, when the handle
 *   loses its capture and when the window is taken off the page under the pointer (closed by its key or by
 *   Escape): each keeps the last box the drag gave.
 * - A window with no frame in the room it stands in (the narrow and phone layouts) gets no handler at all and reads
 *   no kept place or size.
 */

/**
 * Every edge and corner a resize starts from, corners last so they stand over the edges that meet at them.
 */
export const RESIZE_EDGES: readonly ResizeEdge[] = ["top", "right", "bottom", "left", "topLeft", "topRight", "bottomRight", "bottomLeft"];

/**
 * Where a window's place or size is held: the page's setting, which the window follows and its handles change.
 */
export interface WishSource<T> {
  /**
   * Follows the wish.
   *
   * @param listener Told after each change.
   * @returns Stops following.
   */
  readonly subscribe: (listener: () => void) => () => void;
  /**
   * The wish, or null for a window never moved or resized.
   */
  readonly getSnapshot: () => T | null;
  /**
   * Takes a wish, or null to forget it; `keep` is false while a drag is under way.
   */
  readonly set: (value: T | null, keep: boolean) => void;
}

/**
 * Where a window's place is held.
 */
export type PlaceSource = WishSource<WindowPlace>;

/**
 * Where a window's size is held.
 */
export type SizeSource = WishSource<PlaceSize>;

/**
 * A wish held for the life of the component that makes it and kept nowhere, for a window whose page gives no source:
 * it moves and resizes for the visit.
 *
 * @returns The source, empty.
 */
export function heldWish<T>(): WishSource<T> {
  let current: T | null = null;
  const listeners = new Set<() => void>();
  return {
    subscribe: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    getSnapshot: () => current,
    set: (value) => {
      if (JSON.stringify(value) !== JSON.stringify(current)) {
        current = value;
        for (const listener of listeners) {
          listener();
        }
      }
    },
  };
}

/**
 * What a `Window` needs to be moved: the hint its title is described by, and the handlers of its title bar and of
 * its title.
 */
export interface WindowMove {
  readonly hint: string;
  readonly onMouseDown: (event: MouseEvent<HTMLElement>) => void;
  readonly onPointerDown: (event: PointerEvent<HTMLElement>) => void;
  readonly onPointerMove: (event: PointerEvent<HTMLElement>) => void;
  readonly onPointerUp: (event: PointerEvent<HTMLElement>) => void;
  readonly onPointerCancel: (event: PointerEvent<HTMLElement>) => void;
  readonly onLostPointerCapture: (event: PointerEvent<HTMLElement>) => void;
  readonly onDoubleClick: (event: MouseEvent<HTMLElement>) => void;
  readonly onKeyDown: (event: KeyboardEvent<HTMLElement>) => void;
}

/**
 * The handlers of an edge, a corner or the grip, which read the edge they begin from off the element's `data-edge`.
 */
export interface WindowEdges {
  readonly onMouseDown: (event: MouseEvent<HTMLElement>) => void;
  readonly onPointerDown: (event: PointerEvent<HTMLElement>) => void;
  readonly onPointerMove: (event: PointerEvent<HTMLElement>) => void;
  readonly onPointerUp: (event: PointerEvent<HTMLElement>) => void;
  readonly onPointerCancel: (event: PointerEvent<HTMLElement>) => void;
  readonly onLostPointerCapture: (event: PointerEvent<HTMLElement>) => void;
}

/**
 * The grip's handlers: those of an edge, its keys and its focus.
 */
export interface WindowGrip extends WindowEdges {
  readonly label: string;
  readonly onKeyDown: (event: KeyboardEvent<HTMLElement>) => void;
  readonly onFocus: (event: FocusEvent<HTMLElement>) => void;
  readonly onBlur: (event: FocusEvent<HTMLElement>) => void;
}

/**
 * Everything a `Window` needs to be a framed window: its title bar's handlers, its edges' and its grip's, whether it
 * is being resized or its grip has the keyboard (the ring and the size are shown), and the size it is drawn at.
 */
export interface WindowFrame {
  readonly move: WindowMove;
  readonly edges: WindowEdges;
  readonly grip: WindowGrip;
  readonly sizing: boolean;
  readonly size: PlaceSize;
}

/**
 * What `useWindowFrame` is given.
 */
export interface WindowFrameParams {
  readonly id: FramedWindowId;
  readonly place?: PlaceSource | undefined;
  readonly size?: SizeSource | undefined;
  readonly home: FrameBox;
  readonly room: PlaceRoom;
}

/**
 * A move of the title bar under way.
 */
interface MoveDrag {
  readonly pointer: number;
  readonly x: number;
  readonly y: number;
  readonly px: number;
  readonly wish: WindowPlace | null;
  last: WindowPlace | null;
}

/**
 * A resize under way.
 */
interface ResizeDrag {
  readonly pointer: number;
  readonly x: number;
  readonly y: number;
  readonly px: number;
  readonly edge: ResizeEdge;
  readonly start: FrameBox;
  readonly before: FrameWishes;
  last: FrameWishes;
  at: { readonly x: number; readonly y: number } | null;
  frame: number | null;
}

/**
 * Whether a press on the title bar landed on one of its own controls, which the press belongs to.
 *
 * @param event The press.
 * @returns True when the target is inside a button of the title bar.
 */
function onControl(event: { readonly target: EventTarget; readonly currentTarget: HTMLElement }): boolean {
  const control = event.target instanceof Element ? event.target.closest("button") : null;
  return control !== null && event.currentTarget.contains(control);
}

/**
 * Reads the edge a handle begins a resize from.
 *
 * @param element The handle.
 * @returns The edge its `data-edge` names, or null.
 */
function edgeOf(element: HTMLElement): ResizeEdge | null {
  const named = element.dataset["edge"];
  return RESIZE_EDGES.find((edge) => edge === named) ?? null;
}

/**
 * Makes a window framed for as long as the component that calls it is mounted. A drag still under way as the
 * component leaves the page ends there, its last box kept. Called by `PlayWindow` and `ModalWindow` alone, which is
 * what gives a window its frame by the id its page hands it.
 *
 * @param params The window's `id`; where its `place` and its `size` are held (a visit's memory where a page gives
 *   none); `home`, the box its layout gives, whole logical pixels; and the `room` it is held inside.
 * @returns The `box` to draw the window at (the wishes held inside the room, or `home` where neither was made or the
 *   window has no frame) and the `frame` for `Window`, or null where the room cannot hold the window's minimum.
 * @example
 * const { box, frame } = useWindowFrame({ id: "inventory", home: { left: 326, top: 44, width: 300, height: 236 }, room: windowRoom(area) });
 */
export function useWindowFrame({ id, place, size, home, room }: WindowFrameParams): { readonly box: FrameBox; readonly frame: WindowFrame | null } {
  const [own] = useState(() => ({ place: heldWish<WindowPlace>(), size: heldWish<PlaceSize>() }));
  const placeSource = place ?? own.place;
  const sizeSource = size ?? own.size;
  const keyed = useKeyHints();
  const placeWish = useSyncExternalStore(placeSource.subscribe, placeSource.getSnapshot, placeSource.getSnapshot);
  const sizeWish = useSyncExternalStore(sizeSource.subscribe, sizeSource.getSnapshot, sizeSource.getSnapshot);
  const [dragging, setDragging] = useState(false);
  const [focused, setFocused] = useState(false);
  const moving = useRef<MoveDrag | null>(null);
  const resizing = useRef<ResizeDrag | null>(null);
  const held = useRef({ placeSource, sizeSource });
  held.current = { placeSource, sizeSource };
  useEffect(
    () => () => {
      const slide = moving.current;
      moving.current = null;
      if (slide !== null) {
        held.current.placeSource.set(slide.last, true);
      }
      const stretch = resizing.current;
      resizing.current = null;
      if (stretch !== null) {
        if (stretch.frame !== null && typeof cancelAnimationFrame === "function") {
          cancelAnimationFrame(stretch.frame);
        }
        held.current.placeSource.set(stretch.last.place, true);
        held.current.sizeSource.set(stretch.last.size, true);
      }
    },
    [],
  );
  const box = drawFrame(id, home, { place: placeWish, size: sizeWish }, room);
  if (!hasWindowFrame(id, room)) {
    return { box, frame: null };
  }
  const limits = WINDOW_LIMITS[id];
  const homePlace: WindowPlace = { left: home.left, top: home.top };
  const drawn: PlaceSize = { width: box.width, height: box.height };
  const before: FrameWishes = { place: placeWish, size: sizeWish };

  /**
   * Ends a move, keeping a place: the one the release's own position gives, or, when the pointer is taken away (a
   * cancel, or the title bar losing its capture), the last one a move gave, since such an event's coordinates say
   * nothing of where the pointer was. A release has already ended the drag by the time its capture is lost.
   *
   * @param event The release, or the pointer being taken away.
   * @param taken Whether the pointer was taken away.
   */
  const endMove = (event: PointerEvent<HTMLElement>, taken: boolean): void => {
    const open = moving.current;
    if (open === null || open.pointer !== event.pointerId) {
      return;
    }
    moving.current = null;
    placeSource.set(taken ? open.last : dragPlace(open.wish, homePlace, { x: event.clientX - open.x, y: event.clientY - open.y }, open.px, drawn, room), true);
  };

  const move: WindowMove = {
    hint: keyed ? say("window.moveHint") : "",
    /**
     * Keeps a press on the title bar from taking focus or selecting text.
     *
     * @param event The press.
     */
    onMouseDown: (event) => {
      if (!onControl(event)) {
        event.preventDefault();
      }
    },
    /**
     * Starts a drag from the wish as it stands. A press that starts none (another button, a press on the close
     * button, a title bar outside the overlay, where no logical pixel is known) is left as it came.
     *
     * @param event The press.
     */
    onPointerDown: (event) => {
      if (event.button !== 0 || onControl(event)) {
        return;
      }
      const px = cssPerLogical(event.currentTarget);
      if (px === null) {
        return;
      }
      event.preventDefault();
      event.currentTarget.setPointerCapture(event.pointerId);
      moving.current = { pointer: event.pointerId, x: event.clientX, y: event.clientY, px, wish: placeWish, last: placeWish };
    },
    /**
     * Moves the window with the pointer's travel while a drag is under way.
     *
     * @param event The move.
     */
    onPointerMove: (event) => {
      const open = moving.current;
      if (open !== null && open.pointer === event.pointerId) {
        open.last = dragPlace(open.wish, homePlace, { x: event.clientX - open.x, y: event.clientY - open.y }, open.px, drawn, room);
        placeSource.set(open.last, false);
      }
    },
    /**
     * Ends a drag where the pointer was released.
     *
     * @param event The release.
     */
    onPointerUp: (event) => endMove(event, false),
    /**
     * Ends a drag the browser took the pointer from.
     *
     * @param event The cancel.
     */
    onPointerCancel: (event) => endMove(event, true),
    /**
     * Ends a drag whose capture the title bar lost without a release.
     *
     * @param event The loss.
     */
    onLostPointerCapture: (event) => endMove(event, true),
    /**
     * Returns the window to where its layout puts it.
     *
     * @param event The double click on the title bar.
     */
    onDoubleClick: (event) => {
      if (!onControl(event)) {
        placeSource.set(null, true);
      }
    },
    /**
     * The arrow keys move the window and Home returns it; every other key is left alone.
     *
     * @param event The key press on the title.
     */
    onKeyDown: (event) => {
      if (event.altKey || event.ctrlKey || event.metaKey || event.target !== event.currentTarget) {
        return;
      }
      const step = keyStep(event.key, event.shiftKey);
      if (step === null && event.key !== "Home") {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      placeSource.set(step === null ? null : movePlace(placeWish, homePlace, step, drawn, room), true);
    },
  };

  /**
   * Gives the wishes a resize has reached to their sources.
   *
   * @param wishes The place and the size.
   * @param keep Whether to keep them; false while the drag is under way.
   */
  const give = (wishes: FrameWishes, keep: boolean): void => {
    placeSource.set(wishes.place, keep);
    sizeSource.set(wishes.size, keep);
  };

  /**
   * Works a resize out for the pointer at a place and gives it to the sources.
   *
   * @param open The resize.
   * @param x The pointer's left, in CSS pixels.
   * @param y The pointer's top, in CSS pixels.
   * @param keep Whether the result is to be kept.
   */
  const apply = (open: ResizeDrag, x: number, y: number, keep: boolean): void => {
    const next = resizeBox(open.edge, open.start, { x: (x - open.x) / open.px, y: (y - open.y) / open.px }, limits, room);
    open.last = resizeWishes(open.before, home, open.start, next);
    give(open.last, keep);
  };

  /**
   * Ends a resize, keeping what the last move gave, or, where the pointer was released, what its place gives.
   *
   * @param event The release, or the pointer being taken away.
   * @param taken Whether the pointer was taken away.
   */
  const endResize = (event: PointerEvent<HTMLElement>, taken: boolean): void => {
    const open = resizing.current;
    if (open === null || open.pointer !== event.pointerId) {
      return;
    }
    resizing.current = null;
    if (open.frame !== null && typeof cancelAnimationFrame === "function") {
      cancelAnimationFrame(open.frame);
    }
    setDragging(false);
    if (!taken) {
      apply(open, event.clientX, event.clientY, true);
    } else if (open.at !== null) {
      apply(open, open.at.x, open.at.y, true);
    } else {
      give(open.last, true);
    }
  };

  const edges: WindowEdges = {
    /**
     * Keeps a press on an edge from taking focus or selecting text.
     *
     * @param event The press.
     */
    onMouseDown: (event) => event.preventDefault(),
    /**
     * Starts a resize from the drawn box, the edge being the handle's.
     *
     * @param event The press.
     */
    onPointerDown: (event) => {
      const edge = edgeOf(event.currentTarget);
      if (event.button !== 0 || edge === null) {
        return;
      }
      const px = cssPerLogical(event.currentTarget);
      if (px === null) {
        return;
      }
      event.preventDefault();
      event.currentTarget.setPointerCapture(event.pointerId);
      resizing.current = { pointer: event.pointerId, x: event.clientX, y: event.clientY, px, edge, start: box, before, last: before, at: null, frame: null };
      setDragging(true);
    },
    /**
     * Notes where the pointer is and works the resize out once a frame.
     *
     * @param event The move.
     */
    onPointerMove: (event) => {
      const open = resizing.current;
      if (open === null || open.pointer !== event.pointerId) {
        return;
      }
      open.at = { x: event.clientX, y: event.clientY };
      if (open.frame !== null) {
        return;
      }
      /**
       * Works the resize out for where the pointer last was.
       */
      const flush = (): void => {
        open.frame = null;
        const at = open.at;
        open.at = null;
        if (at !== null && resizing.current === open) {
          apply(open, at.x, at.y, false);
        }
      };
      if (typeof requestAnimationFrame === "function") {
        open.frame = requestAnimationFrame(flush);
      } else {
        flush();
      }
    },
    /**
     * Ends a resize where the pointer was released.
     *
     * @param event The release.
     */
    onPointerUp: (event) => endResize(event, false),
    /**
     * Ends a resize the browser took the pointer from.
     *
     * @param event The cancel.
     */
    onPointerCancel: (event) => endResize(event, true),
    /**
     * Ends a resize whose capture the handle lost without a release.
     *
     * @param event The loss.
     */
    onLostPointerCapture: (event) => endResize(event, true),
  };

  const grip: WindowGrip = {
    ...edges,
    label: say(keyed ? "window.gripLabel" : "window.gripLabelTouch"),
    /**
     * The arrow keys size the window and Home returns its size; every other key is left alone.
     *
     * @param event The key press on the grip.
     */
    onKeyDown: (event) => {
      if (event.altKey || event.ctrlKey || event.metaKey) {
        return;
      }
      const next = event.key === "Home" ? null : keyResizeBox(event.key, event.shiftKey, box, limits, room);
      if (next === null && event.key !== "Home") {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      give(next === null ? { place: placeWish, size: null } : resizeWishes(before, home, box, next), true);
    },
    /**
     * Shows the ring and the size when the keyboard, not the pointer, brought focus here.
     *
     * @param event The focus.
     */
    onFocus: (event) => {
      if (event.currentTarget.matches(":focus-visible")) {
        setFocused(true);
      }
    },
    /**
     * Hides them again.
     */
    onBlur: () => setFocused(false),
  };
  return { box, frame: { move, edges, grip, sizing: dragging || focused, size: drawn } };
}
